import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { PageHeader } from '@/shell/AppShell'
import { api } from '@/lib/api'
import { BarChart, type BarDatum } from '@/components/BarChart'
import { ErrorState, Skeleton } from '@/components/States'
import { CsvButton } from '@/components/CsvButton'
import { RefreshButton } from '@/components/RefreshButton'
import { downloadCsv } from '@/lib/csv'

// A16 — funnel/daromad blokidagi analitika ekranlari. Avval «halol skelet» edi
// (ClickHouse/event-ETL kutardi); MyGram'da event-ETL yo'q, lekin `updates` (real
// per-user event oqimi), `messages`, `wallet_txns`, `referrals`, `users.is_premium`
// jonli Postgres-agregatlarini beradi. HAMMA raqam jonli so'rov — soxta ma'lumot
// yo'q. Aniq event-ETL yetmagan joyda «Postgres-taxminiy» deb belgilanadi.
// Modul-lokal (adapters.ts / i18n HOT emas): matnlar shu faylda konstanta.

const STR = {
  refresh: 'Yangilash',
  export: 'CSV',
  noData: "Ma'lumot yo'q",
  values: 'Qiymatlar',
  approx: 'Postgres-taxminiy',
  // A16.13
  screenTime: "Ekran bo'yicha vaqt",
  screenTimeNote: "updates.type faollik-oralig'i proksisi (Postgres-taxminiy)",
  screens: 'Ekranlar',
  sessions: 'Sessiyalar',
  avgTime: "O'rtacha vaqt",
  topScreen: 'Eng faol',
  // A16.15
  funnelBuilder: 'Funnel konstruktori',
  funnelNote: "Ro'yxat → profil → birinchi xabar → faol → ushlab qolindi (users/messages/updates)",
  step: 'Qadam',
  users: 'Foydalanuvchi',
  dropoff: 'Yo‘qolish',
  // A16.25
  ltv: 'LTV (umr qiymati)',
  ltvNote: 'Signup-oy kohorti × hamyon kirimi/foydalanuvchi (Postgres-taxminiy)',
  cohort: 'Kohort',
  payers: "To'lovchilar",
  avgLtv: "O'rtacha LTV",
  totalPayers: "Jami to'lovchilar",
  // A16.26
  conversionFunnel: 'Konversiya voronkasi',
  conversionNote: "Ro'yxat → xabar → hamyon to'ldirish → sarf (users/messages/wallet_txns)",
  rate: 'Ulush',
  // A16.28
  viral: 'Viral koeffitsient',
  viralNote: 'Referral asosidagi K (referrals jadvali; taklif-urinish telemetriyasi yo‘q)',
  overallK: 'Umumiy K',
  totalReferrals: 'Jami takliflar',
  referrers: 'Taklif qiluvchilar',
  invitesAccepted: 'Qabul qilindi',
  // A16.30
  premiumConversion: 'Premium konversiyasi',
  premiumNote: 'users.is_premium ulushi (davlat kesimida)',
  segment: 'Segment',
  trials: 'Baza',
  conversions: 'Premium',
  overallRate: 'Umumiy ulush',
} as const

// ---------- shared local presentational bits ----------

function useLoad<T>(fetcher: (s?: AbortSignal) => Promise<T>, reloadKey: number) {
  const [data, setData] = useState<T | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setLoading(true)
    setError(null)
    fetcher(ac.signal)
      .then((d) => {
        if (alive) setData(d)
      })
      .catch((e: unknown) => {
        if (!alive || (e instanceof DOMException && e.name === 'AbortError')) return
        setError(e instanceof Error ? e : new Error(String(e)))
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
      ac.abort()
    }
  }, [fetcher, reloadKey])
  return { data, loading, error }
}

function KPI({ label, value, accent }: { label: string; value: ReactNode; accent?: boolean }) {
  return (
    <div className="rounded-lg border border-border-default bg-surface-2 p-3">
      <div className="text-xs text-tertiary">{label}</div>
      <div className={`mt-0.5 font-mono text-2xl tabular ${accent ? 'text-accent' : 'text-primary'}`}>{value}</div>
    </div>
  )
}

function Panel({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="max-w-3xl rounded-lg border border-border-default bg-surface-2 p-3">
      <div className="mb-2 text-sm font-medium text-secondary">{title}</div>
      {children}
    </div>
  )
}

/** Funnel/qadamlar chizig'i — kenglik birinchi qadamga nisbatan proporsional. */
function FunnelBars({ steps }: { steps: { label: string; value: number; sub?: string }[] }) {
  const max = Math.max(1, ...steps.map((s) => s.value))
  return (
    <div className="space-y-1.5">
      {steps.map((s, i) => (
        <div key={i} className="flex items-center gap-2">
          <div className="w-44 shrink-0 truncate text-sm text-secondary" title={s.label}>
            {s.label}
          </div>
          <div className="relative h-7 flex-1 overflow-hidden rounded bg-surface-3">
            <div
              className="h-full rounded bg-[var(--chart-1)]"
              style={{ width: `${(s.value / max) * 100}%`, transition: 'width 200ms' }}
            />
            <div className="absolute inset-0 flex items-center gap-2 px-2 font-mono text-xs tabular text-primary">
              <span>{s.value.toLocaleString()}</span>
              {s.sub && <span className="text-tertiary">· {s.sub}</span>}
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

function SimpleTable({ head, rows }: { head: string[]; rows: (string | number)[][] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr>
            {head.map((h, i) => (
              <th
                key={i}
                className={`border-b border-border-default px-2 py-1.5 font-medium text-tertiary ${i === 0 ? 'text-left' : 'text-right'}`}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, ri) => (
            <tr key={ri}>
              {r.map((c, ci) => (
                <td
                  key={ci}
                  className={`border-b border-border-subtle px-2 py-1.5 ${
                    ci === 0 ? 'text-left text-secondary' : 'text-right font-mono tabular text-primary'
                  }`}
                >
                  {typeof c === 'number' ? c.toLocaleString() : c}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function ScreenScaffold({
  title,
  id,
  note,
  onReload,
  onExport,
  children,
}: {
  title: string
  id: string
  note: string
  onReload: () => void
  onExport?: () => void
  children: ReactNode
}) {
  return (
    <>
      <PageHeader
        title={title}
        screenId={id}
        description={note}
        actions={
          <div className="flex items-center gap-2">
            <RefreshButton onClick={onReload} />
            {onExport && <CsvButton onClick={onExport} />}
          </div>
        }
      />
      <div className="min-h-0 flex-1 space-y-3 overflow-auto p-3">{children}</div>
    </>
  )
}

// ---------- fetchers (typed to the Go analytics_ext handlers) ----------

interface ScreenTimeRow {
  screen: string
  avgSeconds: number
  sessions: number
}
const fetchScreenTime = (s?: AbortSignal) =>
  api.get<{ screens: ScreenTimeRow[] }>('/admin/analytics/screen-time', s ? { signal: s } : undefined).then((d) => d.screens)

interface FunnelStepDef {
  step: string
  event: string
  users: number
  dropoff: number
}
const fetchFunnelBuilder = (s?: AbortSignal) =>
  api.get<{ steps: FunnelStepDef[] }>('/admin/analytics/funnels/build', s ? { signal: s } : undefined).then((d) => d.steps)

interface LtvCohort {
  cohort: string
  ltv: number
  payers: number
}
const fetchLtv = (s?: AbortSignal) =>
  api.get<{ cohorts: LtvCohort[] }>('/admin/analytics/ltv', s ? { signal: s } : undefined).then((d) => d.cohorts)

interface ConversionStep {
  step: string
  users: number
  rate: number
}
const fetchConversionFunnel = (s?: AbortSignal) =>
  api
    .get<{ steps: ConversionStep[] }>('/admin/analytics/conversion-funnel', s ? { signal: s } : undefined)
    .then((d) => d.steps)

interface ViralPoint {
  date: string
  kFactor: number
  invitesSent: number
  invitesAccepted: number
}
interface ViralReport {
  series: ViralPoint[]
  overallK: number
  totalReferrals: number
  referrers: number
}
const fetchViral = (s?: AbortSignal) =>
  api.get<ViralReport>('/admin/analytics/viral', s ? { signal: s } : undefined)

interface PremiumConversionRow {
  plan: string
  trials: number
  conversions: number
  rate: number
}
const fetchPremiumConversion = (s?: AbortSignal) =>
  api
    .get<{ plans: PremiumConversionRow[] }>('/admin/analytics/premium-conversion', s ? { signal: s } : undefined)
    .then((d) => d.plans)

function fmtDuration(sec: number): string {
  const s = Math.round(sec)
  if (s < 60) return `${s}s`
  const m = Math.floor(s / 60)
  const r = s % 60
  return `${m}m ${r}s`
}

// ---------- A16.13 Screen-time ----------

export function ScreenTime() {
  const [reload, setReload] = useState(0)
  const { data, loading, error } = useLoad(fetchScreenTime, reload)
  const bars = useMemo<BarDatum[]>(() => (data ?? []).map((r) => ({ label: r.screen, value: r.sessions })), [data])
  const totalSessions = (data ?? []).reduce((a, r) => a + r.sessions, 0)
  const top = data?.[0]?.screen ?? '—'
  const onExport = () =>
    data && downloadCsv('screen-time', [[STR.screens, STR.sessions, STR.avgTime], ...data.map((r) => [r.screen, r.sessions, r.avgSeconds])])
  if (error) return <ErrorState code={error.message} onRetry={() => setReload((n) => n + 1)} />
  return (
    <ScreenScaffold title={STR.screenTime} id="A16.13" note={STR.screenTimeNote} onReload={() => setReload((n) => n + 1)} onExport={data ? onExport : undefined}>
      <div className="grid max-w-3xl grid-cols-2 gap-2 md:grid-cols-3">
        <KPI label={STR.screens} value={data?.length ?? '—'} accent />
        <KPI label={STR.sessions} value={loading ? '—' : totalSessions.toLocaleString()} />
        <KPI label={STR.topScreen} value={<span className="text-lg">{top}</span>} />
      </div>
      <Panel title={STR.sessions}>
        {loading ? <Skeleton className="h-40 w-full" /> : <BarChart data={bars} emptyLabel={STR.noData} tableLabel={STR.values} />}
      </Panel>
      <Panel title={STR.screenTime}>
        {loading ? (
          <Skeleton className="h-40 w-full" />
        ) : (
          <SimpleTable
            head={[STR.screens, STR.sessions, STR.avgTime]}
            rows={(data ?? []).map((r) => [r.screen, r.sessions, fmtDuration(r.avgSeconds)])}
          />
        )}
      </Panel>
    </ScreenScaffold>
  )
}

// ---------- A16.15 Funnel-builder ----------

export function FunnelBuilder() {
  const [reload, setReload] = useState(0)
  const { data, loading, error } = useLoad(fetchFunnelBuilder, reload)
  const onExport = () =>
    data && downloadCsv('funnel-builder', [[STR.step, STR.users, STR.dropoff], ...data.map((r) => [r.step, r.users, `${r.dropoff}%`])])
  if (error) return <ErrorState code={error.message} onRetry={() => setReload((n) => n + 1)} />
  return (
    <ScreenScaffold title={STR.funnelBuilder} id="A16.15" note={STR.funnelNote} onReload={() => setReload((n) => n + 1)} onExport={data ? onExport : undefined}>
      <Panel title={STR.funnelBuilder}>
        {loading ? (
          <Skeleton className="h-40 w-full" />
        ) : (
          <FunnelBars steps={(data ?? []).map((r) => ({ label: r.step, value: r.users, sub: r.dropoff > 0 ? `−${r.dropoff}%` : undefined }))} />
        )}
      </Panel>
    </ScreenScaffold>
  )
}

// ---------- A16.25 LTV ----------

export function Ltv() {
  const [reload, setReload] = useState(0)
  const { data, loading, error } = useLoad(fetchLtv, reload)
  const bars = useMemo<BarDatum[]>(() => (data ?? []).map((c) => ({ label: c.cohort, value: c.ltv })), [data])
  const totalPayers = (data ?? []).reduce((a, c) => a + c.payers, 0)
  const avgLtv = data && data.length ? data.reduce((a, c) => a + c.ltv, 0) / data.length : 0
  const onExport = () => data && downloadCsv('ltv', [[STR.cohort, STR.ltv, STR.payers], ...data.map((c) => [c.cohort, c.ltv, c.payers])])
  if (error) return <ErrorState code={error.message} onRetry={() => setReload((n) => n + 1)} />
  return (
    <ScreenScaffold title={STR.ltv} id="A16.25" note={STR.ltvNote} onReload={() => setReload((n) => n + 1)} onExport={data ? onExport : undefined}>
      <div className="grid max-w-3xl grid-cols-2 gap-2 md:grid-cols-3">
        <KPI label={STR.avgLtv} value={loading ? '—' : avgLtv.toLocaleString(undefined, { maximumFractionDigits: 1 })} accent />
        <KPI label={STR.totalPayers} value={loading ? '—' : totalPayers.toLocaleString()} />
        <KPI label={STR.cohort} value={data?.length ?? '—'} />
      </div>
      <Panel title={STR.ltv}>
        {loading ? <Skeleton className="h-40 w-full" /> : <BarChart data={bars} emptyLabel={STR.noData} tableLabel={STR.values} />}
      </Panel>
      <Panel title={STR.payers}>
        {loading ? (
          <Skeleton className="h-40 w-full" />
        ) : (
          <SimpleTable head={[STR.cohort, STR.ltv, STR.payers]} rows={(data ?? []).map((c) => [c.cohort, c.ltv, c.payers])} />
        )}
      </Panel>
    </ScreenScaffold>
  )
}

// ---------- A16.26 Conversion funnel ----------

export function ConversionFunnel() {
  const [reload, setReload] = useState(0)
  const { data, loading, error } = useLoad(fetchConversionFunnel, reload)
  const onExport = () => data && downloadCsv('conversion-funnel', [[STR.step, STR.users, STR.rate], ...data.map((r) => [r.step, r.users, `${r.rate}%`])])
  if (error) return <ErrorState code={error.message} onRetry={() => setReload((n) => n + 1)} />
  return (
    <ScreenScaffold title={STR.conversionFunnel} id="A16.26" note={STR.conversionNote} onReload={() => setReload((n) => n + 1)} onExport={data ? onExport : undefined}>
      <Panel title={STR.conversionFunnel}>
        {loading ? (
          <Skeleton className="h-40 w-full" />
        ) : (
          <FunnelBars steps={(data ?? []).map((r) => ({ label: r.step, value: r.users, sub: `${r.rate}%` }))} />
        )}
      </Panel>
    </ScreenScaffold>
  )
}

// ---------- A16.28 Viral coefficient ----------

export function ViralCoefficient() {
  const [reload, setReload] = useState(0)
  const { data, loading, error } = useLoad(fetchViral, reload)
  const bars = useMemo<BarDatum[]>(() => (data?.series ?? []).map((p) => ({ label: p.date, value: p.invitesAccepted })), [data])
  const onExport = () =>
    data &&
    downloadCsv('viral', [
      [STR.overallK, STR.totalReferrals, STR.referrers],
      [data.overallK, data.totalReferrals, data.referrers],
      [],
      ['date', STR.invitesAccepted, STR.referrers, 'K'],
      ...data.series.map((p) => [p.date, p.invitesAccepted, p.invitesSent, p.kFactor]),
    ])
  if (error) return <ErrorState code={error.message} onRetry={() => setReload((n) => n + 1)} />
  return (
    <ScreenScaffold title={STR.viral} id="A16.28" note={STR.viralNote} onReload={() => setReload((n) => n + 1)} onExport={data ? onExport : undefined}>
      <div className="grid max-w-3xl grid-cols-2 gap-2 md:grid-cols-3">
        <KPI label={STR.overallK} value={loading ? '—' : (data?.overallK ?? 0).toFixed(2)} accent />
        <KPI label={STR.totalReferrals} value={loading ? '—' : (data?.totalReferrals ?? 0).toLocaleString()} />
        <KPI label={STR.referrers} value={loading ? '—' : (data?.referrers ?? 0).toLocaleString()} />
      </div>
      <Panel title={STR.invitesAccepted}>
        {loading ? <Skeleton className="h-40 w-full" /> : <BarChart data={bars} emptyLabel={STR.noData} tableLabel={STR.values} />}
      </Panel>
    </ScreenScaffold>
  )
}

// ---------- A16.30 Premium conversion ----------

export function PremiumConversion() {
  const [reload, setReload] = useState(0)
  const { data, loading, error } = useLoad(fetchPremiumConversion, reload)
  const overall = data && data.length ? data[0] : null
  const segs = data ? data.slice(1) : []
  const bars = useMemo<BarDatum[]>(() => segs.map((r) => ({ label: r.plan, value: r.rate })), [data])
  const onExport = () =>
    data && downloadCsv('premium-conversion', [[STR.segment, STR.trials, STR.conversions, STR.rate], ...data.map((r) => [r.plan, r.trials, r.conversions, `${r.rate}%`])])
  if (error) return <ErrorState code={error.message} onRetry={() => setReload((n) => n + 1)} />
  return (
    <ScreenScaffold title={STR.premiumConversion} id="A16.30" note={STR.premiumNote} onReload={() => setReload((n) => n + 1)} onExport={data ? onExport : undefined}>
      <div className="grid max-w-3xl grid-cols-2 gap-2 md:grid-cols-3">
        <KPI label={STR.overallRate} value={loading ? '—' : `${overall?.rate ?? 0}%`} accent />
        <KPI label={STR.conversions} value={loading ? '—' : (overall?.conversions ?? 0).toLocaleString()} />
        <KPI label={STR.trials} value={loading ? '—' : (overall?.trials ?? 0).toLocaleString()} />
      </div>
      <Panel title={STR.rate}>
        {loading ? <Skeleton className="h-40 w-full" /> : <BarChart data={bars} emptyLabel={STR.noData} tableLabel={STR.values} />}
      </Panel>
      <Panel title={STR.segment}>
        {loading ? (
          <Skeleton className="h-40 w-full" />
        ) : (
          <SimpleTable head={[STR.segment, STR.trials, STR.conversions, STR.rate]} rows={(data ?? []).map((r) => [r.plan, r.trials, r.conversions, `${r.rate}%`])} />
        )}
      </Panel>
    </ScreenScaffold>
  )
}
